<template>
  <form @submit.prevent="handleSignIn">
    <FormGroup>
      <View direction="column" gap="sm">
        <Text variant="h1">Welcome back</Text>
        <Text variant="body-large" color="secondary">Log in to start using the app</Text>
      </View>

      <FormField label="Email" :error="form.fieldErrors('email')">
        <Input v-model="form.fields.email" name="email" type="email" placeholder="Type your email" />
      </FormField>

      <View direction="column" gap="sm">
        <FormField label="Password" :error="form.fieldErrors('password')">
          <Input v-model="form.fields.password" name="password" type="password" placeholder="Type your password" />
        </FormField>

        <Text variant="h5" as="div" align="end">
          <Link color="accent" :to="AppRoutes.RESET_PASSWORD">Forgot your password?</Link>
        </Text>
      </View>

      <Button type="submit" :loading="form.submitting">Log in</Button>

      <FormSeparator />
      <InvestorGoogleSignInButton :flow="'sign-in'" />

      <Text v-if="isSignUpAvailable" as="div" variant="body-large" align="center">
        <span class="sign-up-hint">Don't have an account?&nbsp;</span>
        <Link :to="signUpRoute()" :color="'accent'">Sign up</Link>
      </Text>
    </FormGroup>
  </form>
</template>

<script setup lang="ts">
import { email, required } from '@vuelidate/validators';
import { AxiosError } from 'axios';
import { useRouter } from 'vue-router';

import { seatRemovedErrorCode, useAuthStore } from '@tallo/auth';
import {
  Button,
  FormField,
  FormGroup,
  FormSeparator,
  Input,
  Link,
  Text,
  View,
  readableServerMessage,
  serverErrorCode,
  useForm,
} from '@tallo/design-system';
import { AppRoutes, signUpRoute, useNavigation } from '@tallo/investor/navigation';
import { UserRole } from '@tallo/user';

import InvestorGoogleSignInButton from './investor-google-sign-in-button.vue';

const { openMissingPasswordSignIn } = useNavigation();
const router = useRouter();
const authStore = useAuthStore();

const isSignUpAvailable = true;
const form = useForm(
  {
    email: '',
    password: '',
  },
  {
    email: { required, email },
    password: { required },
  },
);

async function handleSignIn() {
  form.submit(async () => {
    try {
      await authStore.signInWithCredentials({
        email: form.fields.email,
        password: form.fields.password,
        requiredRole: UserRole.INVESTOR,
      });

      // Resume the link the guard bounced to sign-in from, if any. A 2FA-gated sign-in is redirected
      // to the code screen by the router guard, which remembers it again and resumes it after the code.
      await router.push(authStore.consumePostSignInRedirect() ?? { path: '/' });
    } catch (error) {
      console.error(error);

      let message = 'Something went wrong. Please try again later';

      if (error instanceof AxiosError) {
        switch (error.response?.status) {
          case 400:
          case 401:
            message = 'Invalid email or password';
            break;

          case 403:
            message =
              serverErrorCode(error) === seatRemovedErrorCode
                ? (readableServerMessage(error) ??
                  'Your seat on the team was removed. Ask an admin to invite you again.')
                : 'Your account is not active. Try to sign up again';
            break;

          case 405:
            openMissingPasswordSignIn({ email: form.fields.email });
            break;

          case 409:
            message = 'Please sign in at "homebase.tallo.ai" to continue.';
            break;
        }
      }

      form.setFieldError('password', message);
    }
  });
}
</script>
